/**
 * PRODUCT-PIM-151A-R2 / R7.2.2D — merge locally published canonical products into Store V2 catalog.
 * Source: /api/store-v2/local-products (Pages → Worker R2 projection). Never Prom.
 * price_uah on Customer rows must be payable whole-UAH (ceil_whole_uah); never raw Prom overwrite.
 */
import type { CatalogV2Product } from "./catalog-v2-types.js";
import { buildSearchHaystack } from "./search-v2.js";
import { formatUah, getCustomerSalePrice } from "./money-v2.js";

export type CustomerLocalProductRow = {
  id: string;
  sku: string;
  title: string;
  title_ru: string | null;
  category: string | null;
  brand: string | null;
  country: string | null;
  image_url: string | null;
  /** Customer payable (ceil_whole_uah for ordinary retail). */
  price_uah: number | null;
  old_price_uah: number | null;
  prom_raw_price_uah?: number | null;
  source_price_uah?: number | null;
  canonical_price_uah?: number | null;
  customer_payable_price_uah?: number | null;
  quantity: number | null;
  availability: string | null;
  is_new: boolean;
  published_locally_at: string | null;
  keywords: string | null;
  short_description: string | null;
  /**
   * AP3R3 — searchable document (may include capped full_description).
   * Indexed into `product.search` only; never rendered as PDP/card body.
   */
  search_text?: string | null;
  weight_volume: string | null;
  prom_external_id: string | null;
  ean?: string | null;
  barcode_gtin?: string | null;
  barcode_provenance?: string | null;
  source_system: "asiastyle_local_canonical";
};

export type CustomerLocalProductsFile = {
  schema_version: "customer_local_products_v1";
  updated_at: string;
  cache_version: string;
  new_window_days: number;
  products: CustomerLocalProductRow[];
  by_category: Record<string, string[]>;
  by_brand: Record<string, string[]>;
  by_country: Record<string, string[]>;
  new_products: string[];
};

let cache: Promise<CustomerLocalProductsFile | null> | null = null;
/** Last observed projection freshness authority (body or response header). */
let cachedVersion: string | null = null;

export function invalidateCustomerLocalProductsCache(): void {
  cache = null;
  cachedVersion = null;
}

export function getCustomerLocalProductsCachedVersion(): string | null {
  return cachedVersion;
}

async function fetchLocalProducts(): Promise<CustomerLocalProductsFile | null> {
  try {
    const res = await fetch(`/api/store-v2/local-products?t=${Date.now()}`, {
      headers: { accept: "application/json" },
      cache: "no-store",
    });
    if (!res.ok) return null;
    const json = (await res.json()) as CustomerLocalProductsFile & { ok?: boolean };
    if (json?.schema_version !== "customer_local_products_v1") return null;
    const headerVersion = String(
      res.headers.get("X-AsiaStyle-Local-Products-Cache-Version") ?? "",
    ).trim();
    const version = headerVersion || String(json.cache_version ?? "").trim() || null;
    cachedVersion = version;
    return json;
  } catch {
    return null;
  }
}

/**
 * AP3 — soft in-memory cache with freshness revalidation.
 * When force=true OR cache empty → network fetch.
 * When cached: return cached immediately, but if caller asks revalidate, replace on version change.
 */
export async function loadCustomerLocalProducts(opts?: {
  force?: boolean;
  revalidate?: boolean;
}): Promise<CustomerLocalProductsFile | null> {
  if (opts?.force) invalidateCustomerLocalProductsCache();
  if (!cache) {
    cache = fetchLocalProducts();
    return cache;
  }
  if (!opts?.revalidate) return cache;
  const prevVersion = cachedVersion;
  const fresh = await fetchLocalProducts();
  if (
    fresh &&
    (!prevVersion || !fresh.cache_version || fresh.cache_version !== prevVersion)
  ) {
    cache = Promise.resolve(fresh);
  }
  return cache;
}

/** Normalize any inbound local price through Customer payable policy. */
export function normalizeLocalPayablePriceUah(raw: number | null | undefined): number | null {
  if (raw == null || !Number.isFinite(Number(raw))) return null;
  const n = Number(raw);
  if (!(n > 0)) return null;
  return getCustomerSalePrice(n);
}

/** Exported for AP3R1 behavioral identity/projection tests. */
export function rowToProduct(row: CustomerLocalProductRow): CatalogV2Product {
  const qty = row.quantity != null ? Math.max(0, Math.trunc(Number(row.quantity))) : null;
  const availability = String(row.availability ?? "").toLowerCase();
  const outOfStock =
    availability === "out_of_stock" || (qty != null && qty <= 0);
  // Prefer explicit payable field; always re-normalize so raw Prom cannot leak.
  const price = normalizeLocalPayablePriceUah(
    row.customer_payable_price_uah ?? row.price_uah,
  );
  const oldPrice = normalizeLocalPayablePriceUah(row.old_price_uah);
  const barcode =
    String(row.barcode_gtin ?? row.ean ?? "").trim() || null;
  // AP3R1 — blank Store SKU must stay blank (never canonical/Prom id).
  const storeSku = String(row.sku ?? "").trim() || null;
  const product: CatalogV2Product = {
    id: row.id,
    sku: storeSku ?? "",
    title: row.title,
    titleRu: row.title_ru,
    categoryKeys: row.category ? [row.category] : [],
    categoryName: row.category ?? "",
    imageUrl: row.image_url,
    productUrl: null,
    priceUah: price,
    displayPrice: price != null ? formatUah(price) : "—",
    isPriced: price != null && price > 0,
    stockStatus: outOfStock ? "out_of_stock" : "in_stock",
    isSellable: !outOfStock && price != null && price > 0,
    sellabilityReason: outOfStock ? "out_of_stock" : "local_canonical",
    brand: row.brand,
    country: row.country,
    weight: row.weight_volume,
    regularPriceUah: oldPrice,
    salePriceUah: null,
    saleStartsAt: null,
    saleEndsAt: null,
    promotionId: null,
    promotionStatus: null,
    priceUpdatedAt: row.published_locally_at,
    sourceRevision: "local_canonical_v1",
    search: "",
    promProductId: row.prom_external_id,
    merchantSku: storeSku,
    merchantArticle: null,
    barcodeGtin: barcode,
    barcodeVerification: barcode ? "manually_verified" : "absent",
    manufacturerPartNumber: null,
    warehouseCode: null,
    sourceSystem: "asiastyle_local_canonical",
    sourceUpdatedAt: row.published_locally_at,
    lastSyncedAt: row.published_locally_at,
    firstSeenAt: row.published_locally_at,
    catalogArrivedAt: row.published_locally_at,
    sourceTitle: row.title,
    customerTitle: row.title,
    titleAliases: row.keywords ? row.keywords.split(/[,;]+/).map((s) => s.trim()).filter(Boolean) : [],
  };
  product.search = buildSearchHaystack({
    title: product.title,
    titleRu: product.titleRu,
    categoryName: product.categoryName,
    sku: product.merchantSku ?? product.sku,
    brand: product.brand,
    country: product.country,
    barcode: product.barcodeGtin,
    promProductId: product.promProductId,
    aliases: product.titleAliases,
    shortDescription: row.short_description,
    searchText: row.search_text ?? null,
  });
  return product;
}

/**
 * Merge local publish into catalog.
 * Local may override Customer price only with a value already normalized via payable policy.
 * Never inject raw Prom fractional into sell-price field.
 */
export async function enrichCatalogWithLocalProducts(
  products: CatalogV2Product[],
): Promise<CatalogV2Product[]> {
  const file = await loadCustomerLocalProducts({ revalidate: true });
  if (!file?.products?.length) return products;
  const byId = new Map(products.map((p) => [p.id, p]));
  const merged = [...products];
  for (const row of file.products) {
    const next = rowToProduct(row);
    const existing = byId.get(row.id);
    if (existing) {
      const idx = merged.findIndex((p) => p.id === row.id);
      if (idx >= 0) {
        const existingPrice =
          typeof existing.priceUah === "number" && Number.isFinite(existing.priceUah)
            ? existing.priceUah
            : null;
        const localPayable = next.priceUah;
        // Prefer normalized local payable when present; otherwise keep catalog.
        // If both exist and local looks like raw fractional while catalog is whole, keep catalog.
        let priceUah = localPayable ?? existingPrice;
        if (
          localPayable != null &&
          existingPrice != null &&
          existingPrice >= 1 &&
          Number.isInteger(existingPrice) &&
          !Number.isInteger(localPayable) &&
          localPayable >= 1
        ) {
          // Defense in depth if an old projection still emits raw fractions.
          priceUah = getCustomerSalePrice(localPayable);
        } else if (localPayable != null) {
          priceUah = getCustomerSalePrice(localPayable);
        } else if (existingPrice != null) {
          priceUah = getCustomerSalePrice(existingPrice);
        }
        const combined: CatalogV2Product = {
          ...existing,
          ...next,
          priceUah,
          displayPrice: priceUah != null ? formatUah(priceUah) : existing.displayPrice ?? next.displayPrice,
          isPriced: priceUah != null && priceUah > 0,
          isSellable: next.isSellable && priceUah != null && priceUah > 0,
        };
        combined.search = buildSearchHaystack({
          title: combined.title,
          titleRu: combined.titleRu,
          categoryName: combined.categoryName,
          sku: combined.merchantSku ?? combined.sku,
          brand: combined.brand,
          country: combined.country,
          barcode: combined.barcodeGtin,
          promProductId: combined.promProductId,
          aliases: combined.titleAliases,
          shortDescription: row.short_description,
          searchText: row.search_text ?? null,
        });
        merged[idx] = combined;
      }
    } else {
      merged.push(next);
      byId.set(row.id, next);
    }
  }
  return merged;
}

export function localNewProductIds(file: CustomerLocalProductsFile | null): Set<string> {
  return new Set(file?.new_products ?? []);
}
